import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuthStore } from '../store/authStore';
import type { AuthRole } from '../services/api';

interface ProtectedRouteProps {
  children: ReactNode;
  /**
   * Restrict the route to the listed principal kinds. If omitted, any
   * authenticated user passes — same behaviour as the v1 guard.
   *
   * If the user IS authenticated but their role isn't allowed, we
   * redirect them to their natural home (`/min-sag` for debtors,
   * `/lookup` for operators / lender humans) instead of `/login` —
   * bouncing a logged-in user back to the login form is a confusing
   * dead end.
   */
  allowedRoles?: AuthRole[];
}

/**
 * Gate any auth-required route.
 *
 * - Unauthenticated → redirect to `/login` and stash the attempted
 *   path on `state.from` so LoginPage can return them on success.
 * - Authenticated but wrong role → redirect to the home for their
 *   role (no `state.from` — we don't want to bounce them back into a
 *   route they can't access).
 */
export default function ProtectedRoute({
  children,
  allowedRoles,
}: ProtectedRouteProps) {
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const location = useLocation();

  if (!token) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    const home = user.role === 'debtor' ? '/min-sag' : '/lookup';
    return <Navigate to={home} replace />;
  }

  return <>{children}</>;
}
